'use client'

import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'

// CSS imports
import 'video.js/dist/video-js.css'
import 'videojs-mobile-ui/dist/videojs-mobile-ui.css'
import './VideoPlayer.css'

import { initializePlugins } from './plugins'
import type { VideoPlayerProps, VideoPlayerRef } from './types'

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(function VideoPlayer(
  {
    src,
    mimeType,
    poster,
    autoplay = false,
    controls = true,
    responsive = true,
    fluid = false,
    fill = true,
    aspectRatio,
    width,
    height,
    className,
    onReady,
    onPlay,
    onPause,
    onEnded,
    onError,
    onTimeUpdate,
    seekButtons,
    mobileUI = true,
    landscapeFullscreen = true,
    airPlay = true,
    pip = true,
    videoUuid,
    userId: _userId,
    token,
    fallbackSrc,
    fallbackMimeType,
    sessionId,
  },
  ref,
) {
  const videoRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<Player | null>(null)
  const [isHydrated, setIsHydrated] = useState(false)

  // Disable right-click context menu (Phase 1 protection)
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    return false
  }

  // Hydration check for SSR
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Expose player methods via ref
  useImperativeHandle(ref, () => ({
    player: playerRef.current,
    play: () => {
      playerRef.current?.play()
    },
    pause: () => {
      playerRef.current?.pause()
    },
    seek: (time: number) => {
      playerRef.current?.currentTime(time)
    },
    getCurrentTime: () => playerRef.current?.currentTime() ?? 0,
    getDuration: () => playerRef.current?.duration() ?? 0,
    toggleFullscreen: () => {
      if (playerRef.current?.isFullscreen()) {
        playerRef.current.exitFullscreen()
      } else {
        playerRef.current?.requestFullscreen()
      }
    },
    togglePip: async () => {
      const video = playerRef.current?.el().querySelector('video')
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
      } else if (video) {
        await video.requestPictureInPicture()
      }
    },
  }))

  // Initialize player
  useEffect(() => {
    if (!isHydrated || !videoRef.current) return

    // Prevent double initialization
    if (playerRef.current) return

    // Create video element
    const videoElement = document.createElement('video-js')
    videoElement.classList.add('vjs-big-play-centered')

    // Add Phase 1 protections
    videoElement.setAttribute('controlsList', 'nodownload')
    videoElement.setAttribute('disablePictureInPicture', 'false')

    videoRef.current.appendChild(videoElement)

    // Player options
    const options: Parameters<typeof videojs>[1] = {
      autoplay,
      controls,
      responsive,
      fluid,
      fill,
      aspectRatio,
      width,
      height,
      sources: [{ src, type: mimeType }],
      poster,
      playbackRates: [0.5, 1, 1.25, 1.5, 2],
      // iOS: keep video inline on initial play, but allow fullscreen when requested
      playsinline: true,
      controlBar: {
        volumePanel: {
          inline: false, // Use vertical volume slider in tooltip
        },
        pictureInPictureToggle: pip, // Enable/disable PiP button
        playbackRateMenuButton: false, // Hide playback rate
      },
      html5: {
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    }

    const player = videojs(videoElement, options, function onPlayerReady(this: Player) {
      // Set full volume
      this.volume(1)
      this.muted(false)

      // Initialize plugins after player is ready (async)
      initializePlugins(this, videojs, {
        seekButtons: seekButtons ?? { forward: 10, back: 10 },
        mobileUI,
        landscapeFullscreen,
        airPlay,
        pip,
      }).then(() => {
        // Handle autoplay - browsers may block unmuted autoplay
        if (autoplay) {
          const playPromise = this.play()
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // Autoplay was prevented - user needs to click play
              // This is expected behavior on Chrome without user interaction
            })
          }
        }

        // Inject auth token header on HLS key requests.
        // This allows manifests served from CDN (without ?token= in key URI)
        // to still authenticate key fetches via the X-Video-Token header.
        // NOTE: videojs.Vhs.xhr.beforeRequest is a global setting, not per-player.
        // This is fine for CreatorTV (single player per page). If multiple players
        // are ever used on one page, this would need to be scoped differently.
        if (token) {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const vhs = (videojs as any).Vhs
          if (vhs) {
            const origBeforeRequest = vhs.xhr?.beforeRequest
            vhs.xhr.beforeRequest = (options: Record<string, any>) => {
              if (origBeforeRequest) {
                options = origBeforeRequest(options)
              }
              if (options.uri?.includes('/api/keys/')) {
                // Rewrite key URL to current origin so the same R2 manifest
                // works on any environment (staging, production, local dev).
                // The manifest stores the production URL, but the player
                // rewrites to window.location.origin at runtime.
                const keyPath = options.uri.substring(options.uri.indexOf('/api/keys/'))
                options.uri = window.location.origin + keyPath
                options.headers = {
                  ...options.headers,
                  'X-Video-Token': token,
                }
              }
              return options
            }
          }
          /* eslint-enable @typescript-eslint/no-explicit-any */
        }

        onReady?.(this)
      })
    })

    playerRef.current = player

    // Event listeners
    if (onPlay) player.on('play', onPlay)
    if (onPause) player.on('pause', onPause)
    if (onEnded) player.on('ended', onEnded)
    if (onError) player.on('error', () => onError(player.error() as unknown as Error))
    if (onTimeUpdate) {
      player.on('timeupdate', () => onTimeUpdate(player.currentTime() ?? 0))
    }

    // Fallback: if primary source fails, try the fallback URL
    // This handles CDN manifest failures by falling back to the /api/hls/ route
    if (fallbackSrc) {
      let hasTriedFallback = false
      player.on('error', () => {
        if (!hasTriedFallback) {
          hasTriedFallback = true
          player.src({
            src: fallbackSrc,
            type: fallbackMimeType || 'application/x-mpegURL',
          })
        }
      })
    }

    // Cleanup
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose()
        playerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]) // Only run on hydration

  // Update source if it changes
  useEffect(() => {
    if (playerRef.current && src) {
      playerRef.current.src({ src, type: mimeType })
      if (poster) playerRef.current.poster(poster)
    }
  }, [src, mimeType, poster])

  // Heartbeat for session tracking (Phase 1 protection)
  useEffect(() => {
    if (!videoUuid || !token) return

    // Track tab visibility — skip heartbeats when tab is hidden
    const isVisibleRef = { current: document.visibilityState === 'visible' }
    const handleVisibility = () => {
      isVisibleRef.current = document.visibilityState === 'visible'
    }
    document.addEventListener('visibilitychange', handleVisibility)

    const heartbeatInterval = setInterval(async () => {
      if (!isVisibleRef.current) return
      try {
        await fetch('/api/media/heartbeat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videoUuid, token, sessionId }),
        })
      } catch (error) {
        console.error('Heartbeat failed:', error)
      }
    }, 180000) // 3 minutes (was 60 seconds)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      clearInterval(heartbeatInterval)
    }
  }, [videoUuid, token, sessionId])

  // Don't render until hydrated
  if (!isHydrated) {
    return <div className={className} style={{ backgroundColor: '#000' }} />
  }

  return (
    <div
      data-vjs-player
      ref={videoRef}
      className={className}
      onContextMenu={handleContextMenu}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    />
  )
})

export default VideoPlayer
